import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../../config';

const STATUS_LABELS = {
    wait: { text: 'Ожидает оплаты', emoji: '🟡' },
    success: { text: 'Оплачено', emoji: '✅' },
    failed: { text: 'Ошибка / Отменено', emoji: '❌' },
};

const POLL_INTERVAL_MS = 5000;   // 5 секунд
const POLL_MAX_MS = 3 * 60 * 1000; // 3 минуты

const formatRub = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    return `${num.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`;
};

const FrogPage = ({ onShowToast }) => {
    const [amount, setAmount] = useState('');
    const [exchangerId, setExchangerId] = useState('');
    const [provider, setProvider] = useState('freekassa');
    const [paymentMethod, setPaymentMethod] = useState('sbp');
    const [telegramUsername, setTelegramUsername] = useState('');
    const [telegramUserId, setTelegramUserId] = useState('');
    const [wataH2HIdsByMethod, setWataH2HIdsByMethod] = useState({
        sbp: { terminalId: '', publicId: '' },
        card: { terminalId: '', publicId: '' },
    });
    const [loading, setLoading] = useState(false);
    const [paymentUrl, setPaymentUrl] = useState('');
    const [fakeUser, setFakeUser] = useState(null);
    const [txId, setTxId] = useState(null);
    const [txStatus, setTxStatus] = useState(null); // 'wait' | 'success' | 'failed'
    const [orderId, setOrderId] = useState(null);
    const [issuedStars, setIssuedStars] = useState(null);
    const [starsQuote, setStarsQuote] = useState(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [quoteError, setQuoteError] = useState('');

    const pollTimerRef = useRef(null);
    const pollStartRef = useRef(null);
    const quoteAbortRef = useRef(null);

    // Провайдеры с единственным способом оплаты.
    useEffect(() => {
        if (provider === 'platega' && paymentMethod !== 'sbp') {
            setPaymentMethod('sbp');
        } else if (provider === 'lavepay' && paymentMethod !== 'wallet') {
            setPaymentMethod('wallet');
        } else if (provider !== 'lavepay' && paymentMethod === 'wallet') {
            setPaymentMethod('sbp');
        }
    }, [provider, paymentMethod]);

    useEffect(() => {
        const loadBotSettings = async () => {
            try {
                const resp = await fetch(`${API_BASE_URL}/api/settings/bot`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                });
                if (!resp.ok) return;
                const bot = await resp.json();
                const initialProvider = (bot.payment_provider || 'freekassa').toLowerCase();
                if (['freekassa', 'wata', 'wata_h2h', 'liontech', 'platega', 'pear', 'aurapay', 'paritypay', 'lavepay'].includes(initialProvider)) {
                    setProvider(initialProvider);
                }
                const defaultTerminal = bot.wata_h2h_terminal_id || '';
                const defaultPublic = bot.wata_h2h_public_id || '';
                if (defaultTerminal || defaultPublic) {
                    setWataH2HIdsByMethod({
                        sbp: { terminalId: defaultTerminal, publicId: defaultPublic },
                        card: { terminalId: defaultTerminal, publicId: defaultPublic },
                    });
                }
            } catch {
                // noop
            }
        };
        loadBotSettings();
    }, []);

    // Останавливаем polling при размонтировании компонента
    useEffect(() => {
        return () => {
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
            if (quoteAbortRef.current) quoteAbortRef.current.abort();
        };
    }, []);

    // Сверка ₽ → Stars по актуальной цене провайдера (тот же расчёт, что на backend при выдаче)
    useEffect(() => {
        const parsed = parseFloat(amount);
        if (!amount || !Number.isFinite(parsed) || parsed <= 0) {
            setStarsQuote(null);
            setQuoteError('');
            setQuoteLoading(false);
            return undefined;
        }

        const timer = setTimeout(async () => {
            if (quoteAbortRef.current) quoteAbortRef.current.abort();
            const controller = new AbortController();
            quoteAbortRef.current = controller;
            setQuoteLoading(true);
            setQuoteError('');
            try {
                const params = new URLSearchParams({
                    amount_rub: String(parsed),
                    provider,
                });
                const resp = await fetch(`${API_BASE_URL}/api/frog/quote?${params}`, {
                    credentials: 'include',
                    signal: controller.signal,
                });
                const data = await resp.json().catch(() => ({}));
                if (!resp.ok) {
                    throw new Error(data.detail || 'Не удалось рассчитать Stars');
                }
                setStarsQuote(data);
            } catch (err) {
                if (err?.name === 'AbortError') return;
                setStarsQuote(null);
                setQuoteError(err.message || 'Ошибка расчёта');
            } finally {
                if (!controller.signal.aborted) setQuoteLoading(false);
            }
        }, 250);

        return () => clearTimeout(timer);
    }, [amount, provider]);

    const applyExactAmount = () => {
        if (!starsQuote || starsQuote.exact_amount_rub == null) return;
        setAmount(String(Number(starsQuote.exact_amount_rub).toFixed(2)));
    };

    const startPolling = (id) => {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        pollStartRef.current = Date.now();

        pollTimerRef.current = setInterval(async () => {
            // Останавливаем через 3 минуты
            if (Date.now() - pollStartRef.current > POLL_MAX_MS) {
                clearInterval(pollTimerRef.current);
                return;
            }
            try {
                const resp = await fetch(`${API_BASE_URL}/api/frog/status/${id}`, {
                    credentials: 'include',
                });
                if (!resp.ok) return;
                const data = await resp.json();
                setTxStatus(data.status);
                // Прекращаем опрос когда статус терминальный
                if (data.status === 'success' || data.status === 'failed') {
                    clearInterval(pollTimerRef.current);
                }
            } catch {
                // тихо игнорируем сетевые ошибки во время polling
            }
        }, POLL_INTERVAL_MS);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!amount) {
            onShowToast && onShowToast('Укажите сумму');
            return;
        }
        const needsTelegramUsername = provider === 'wata_h2h' || provider === 'wata';
        const cleanUsername = telegramUsername.trim().replace(/^@+/, '');
        if (needsTelegramUsername && !cleanUsername) {
            onShowToast && onShowToast('Укажите Telegram username получателя Stars');
            return;
        }
        setLoading(true);
        setPaymentUrl('');
        setFakeUser(null);
        setTxId(null);
        setTxStatus(null);
        setOrderId(null);
        setIssuedStars(null);
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        const selectedWataIds = wataH2HIdsByMethod[paymentMethod] || { terminalId: '', publicId: '' };

        try {
            const resp = await fetch(`${API_BASE_URL}/api/frog/get-requisites`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    amount_rub: parseFloat(amount),
                    exchanger_id: exchangerId.trim(),
                    provider,
                    payment_method: paymentMethod,
                    wata_terminal_id: provider === 'wata_h2h' ? (selectedWataIds.terminalId || '').trim() : undefined,
                    wata_public_id: provider === 'wata_h2h' ? (selectedWataIds.publicId || '').trim() : undefined,
                    telegram_username: needsTelegramUsername ? cleanUsername : undefined,
                    telegram_user_id:
                        provider === 'wata_h2h' && telegramUserId.trim()
                            ? telegramUserId.trim()
                            : undefined,
                }),
            });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) {
                throw new Error(data.detail || 'Не удалось получить реквизиты');
            }
            setPaymentUrl(data.payment_link);
            setFakeUser({
                tgId: data.fake_tg_id,     // формат: exchanger_id-RANDOM
                username: data.fake_username,
            });
            setTxId(data.id);
            setTxStatus(data.status || 'wait');
            setOrderId(data.order_id);
            setIssuedStars(data.stars ?? null);
            if (data.stars != null) {
                setStarsQuote({
                    amount_rub: Number(amount),
                    price_per_star: data.price_per_star,
                    stars: data.stars,
                    exact_amount_rub: data.exact_amount_rub,
                    difference_rub: data.difference_rub,
                    provider: data.provider || provider,
                });
            }
            onShowToast && onShowToast('Ссылка на оплату успешно получена');
            // Запускаем polling статуса
            if (data.id) startPolling(data.id);
        } catch (err) {
            console.error(err);
            onShowToast && onShowToast(err.message || 'Ошибка при получении реквизитов');
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = async () => {
        if (!paymentUrl) return;
        try {
            await navigator.clipboard.writeText(paymentUrl);
            onShowToast && onShowToast('Ссылка скопирована в буфер обмена');
        } catch {
            onShowToast && onShowToast('Не удалось скопировать ссылку');
        }
    };

    const statusInfo = txStatus ? STATUS_LABELS[txStatus] : null;

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <h2 className="text-2xl font-bold text-slate-800">Получить реквизиты</h2>
            </div>

            <p className="text-sm text-slate-600 max-w-2xl">
                Ручная ссылка создаётся через выбранного провайдера. Для каждого метода backend использует свои креды из админки
                или из переменных окружения. Количество Stars считается как floor(сумма ÷ цена ★) по цене выбранного провайдера.
            </p>

            <form
                onSubmit={handleSubmit}
                className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 space-y-4"
            >
                <div className="grid gap-4 md:grid-cols-2 items-start">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Сумма в рублях
                        </label>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            required
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Например, 150.00"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Сверка сумм
                        </label>
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 min-h-[42px] text-sm text-slate-700">
                            {quoteLoading && <span className="text-slate-400">Считаем…</span>}
                            {!quoteLoading && quoteError && (
                                <span className="text-red-600">{quoteError}</span>
                            )}
                            {!quoteLoading && !quoteError && starsQuote && (
                                <div className="space-y-1">
                                    <p>
                                        Введено: <span className="font-medium">{formatRub(starsQuote.amount_rub)}</span>
                                        {' → '}будет выдано:{' '}
                                        <span className="font-semibold text-indigo-700">
                                            {starsQuote.stars} Stars
                                        </span>
                                        {' → '}точная сумма:{' '}
                                        <span className="font-medium">{formatRub(starsQuote.exact_amount_rub)}</span>
                                    </p>
                                    <p className="text-xs text-slate-500">
                                        Цена 1★: {formatRub(starsQuote.price_per_star)}
                                        {Number(starsQuote.difference_rub) > 0
                                            ? ` · разница с введённой: ${formatRub(starsQuote.difference_rub)}`
                                            : ' · сумма совпадает с количеством Stars'}
                                    </p>
                                    {Number(starsQuote.difference_rub) > 0 && (
                                        <button
                                            type="button"
                                            onClick={applyExactAmount}
                                            className="mt-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 underline underline-offset-2"
                                        >
                                            Подставить точную сумму в поле ({formatRub(starsQuote.exact_amount_rub)})
                                        </button>
                                    )}
                                </div>
                            )}
                            {!quoteLoading && !quoteError && !starsQuote && (
                                <span className="text-slate-400">Введите сумму — покажем Stars и рекомендованную сумму</span>
                            )}
                        </div>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                        ID сделки из обменника (только цифры, можно оставить пустым — сгенерируется автоматически)
                    </label>
                    <input
                        type="text"
                        value={exchangerId}
                        onChange={(e) => {
                            const digitsOnly = e.target.value.replace(/\D/g, '');
                            setExchangerId(digitsOnly);
                        }}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        inputMode="numeric"
                        placeholder="Например, 21515"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                        Платёжный провайдер
                    </label>
                    <select
                        value={provider}
                        onChange={(e) => setProvider(e.target.value)}
                        className="w-full md:w-80 px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                    >
                        <option value="freekassa">FreeKassa</option>
                        <option value="wata_h2h">Wata H2H</option>
                        <option value="liontech">LionTech</option>
                        <option value="platega">Platega</option>
                        <option value="pear">Pear (PayPear)</option>
                        <option value="aurapay">Aurapay</option>
                        <option value="paritypay">ParityPay</option>
                        <option value="lavepay">Lave Pay</option>
                        <option value="wata">Wata (DG)</option>
                    </select>
                    <p className="mt-2 text-xs text-slate-500">
                        Провайдер выбирается только для этой операции /frog и не меняет глобальные настройки бота.
                    </p>
                </div>

                <div key={`${provider}-${paymentMethod}`}>
                    <p className="block text-sm font-medium text-slate-700 mb-2">Способ оплаты</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                        {provider === 'lavepay' ? (
                            <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                                <input
                                    type="radio"
                                    name="frog_payment_method"
                                    value="wallet"
                                    checked={paymentMethod === 'wallet'}
                                    onChange={() => setPaymentMethod('wallet')}
                                    className="text-indigo-600 focus:ring-indigo-500"
                                />
                                Laveshka Mini App
                            </label>
                        ) : (
                            <>
                                <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                                    <input
                                        type="radio"
                                        name="frog_payment_method"
                                        value="sbp"
                                        checked={paymentMethod === 'sbp'}
                                        onChange={() => setPaymentMethod('sbp')}
                                        className="text-indigo-600 focus:ring-indigo-500"
                                    />
                                    СБП (Система быстрых платежей)
                                </label>
                                {provider !== 'platega' && (
                                    <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                                        <input
                                            type="radio"
                                            name="frog_payment_method"
                                            value="card"
                                            checked={paymentMethod === 'card'}
                                            onChange={() => setPaymentMethod('card')}
                                            className="text-indigo-600 focus:ring-indigo-500"
                                        />
                                        Банковская карта РФ
                                    </label>
                                )}
                            </>
                        )}
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                        Для FreeKassa, LionTech, Wata H2H и Aurapay ссылка формируется под выбранный способ.
                        Для Platega в этой форме доступен только СБП.
                        Для Lave Pay способ оплаты выбирается внутри приложения Laveshka.
                        Для Wata (DG) по-прежнему одна ссылка на страницу оплаты.
                    </p>
                </div>

                {provider === 'wata_h2h' && (
                    <div className="grid gap-4 md:grid-cols-2">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                Wata H2H Terminal ID ({paymentMethod === 'sbp' ? 'СБП' : 'Карта'})
                            </label>
                            <input
                                type="text"
                                value={wataH2HIdsByMethod[paymentMethod]?.terminalId || ''}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setWataH2HIdsByMethod((prev) => ({
                                        ...prev,
                                        [paymentMethod]: { ...prev[paymentMethod], terminalId: value },
                                    }));
                                }}
                                className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="UUID терминала"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                Wata H2H Public ID ({paymentMethod === 'sbp' ? 'СБП' : 'Карта'})
                            </label>
                            <input
                                type="text"
                                value={wataH2HIdsByMethod[paymentMethod]?.publicId || ''}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setWataH2HIdsByMethod((prev) => ({
                                        ...prev,
                                        [paymentMethod]: { ...prev[paymentMethod], publicId: value },
                                    }));
                                }}
                                className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="UUID public id"
                            />
                        </div>
                    </div>
                )}

                {(provider === 'wata_h2h' || provider === 'wata') && (
                    <div className="grid gap-4 md:grid-cols-2">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                Telegram username получателя Stars
                            </label>
                            <input
                                type="text"
                                value={telegramUsername}
                                onChange={(e) => setTelegramUsername(e.target.value.replace(/\s/g, ''))}
                                required
                                className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="@nickname или nickname"
                                autoComplete="off"
                            />
                            <p className="mt-1 text-xs text-slate-500">
                                В WATA уйдёт с префиксом @. 5–32 символа: латиница, цифры, _.
                            </p>
                        </div>
                        {provider === 'wata_h2h' && (
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    Telegram userId (опционально)
                                </label>
                                <input
                                    type="text"
                                    value={telegramUserId}
                                    onChange={(e) => setTelegramUserId(e.target.value.replace(/\D/g, ''))}
                                    className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="1234567890"
                                    inputMode="numeric"
                                    autoComplete="off"
                                />
                                <p className="mt-1 text-xs text-slate-500">
                                    Если пусто — возьмём FROG_WATA_USER_ID из env (если задан).
                                </p>
                            </div>
                        )}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium shadow-sm hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {loading ? 'Создание ссылки...' : 'Получить реквизиты'}
                </button>
            </form>

            {paymentUrl && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                    {/* Ссылка на оплату */}
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4">
                        <div className="flex-1">
                            <p className="text-sm font-medium text-slate-700 mb-1">Ссылка на оплату</p>
                            <p className="text-sm text-slate-700 break-all">{paymentUrl}</p>
                        </div>
                        <button
                            type="button"
                            onClick={handleCopy}
                            className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm font-medium hover:bg-slate-100"
                        >
                            Копировать
                        </button>
                    </div>

                    {/* Статус оплаты */}
                    {statusInfo && (
                        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
                            ${txStatus === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
                              txStatus === 'failed'  ? 'bg-red-50 text-red-700 border border-red-200' :
                                                       'bg-yellow-50 text-yellow-700 border border-yellow-200'}`}>
                            <span>{statusInfo.emoji}</span>
                            <span>{statusInfo.text}</span>
                            {txStatus === 'wait' && (
                                <span className="ml-1 text-xs text-slate-400">(обновляется автоматически)</span>
                            )}
                        </div>
                    )}

                    {/* Данные пользователя + ID заказа */}
                    {fakeUser && (
                        <div className="text-xs text-slate-500 space-y-0.5">
                            <div>TG ID пользователя: <span className="font-mono font-medium text-slate-700">{fakeUser.tgId}</span></div>
                            {orderId && (
                                <div>ID заказа в CRM: <span className="font-mono font-medium text-slate-700">#{orderId}</span></div>
                            )}
                            {issuedStars != null && (
                                <div>
                                    К выдаче Stars:{' '}
                                    <span className="font-mono font-medium text-slate-700">{issuedStars}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FrogPage;
